import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Session, SessionsService } from '../../services/sessions.service';
import { Button, ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MenuModule } from 'primeng/menu';
import { InputGroupModule } from 'primeng/inputgroup';
import { FluidModule } from 'primeng/fluid';
import { MenuItem } from 'primeng/api';
import { StyleClassModule } from 'primeng/styleclass';
import { SessionSignInFlag } from '../session-signin/session-signin.component';

/**
 * Component responsible for displaying and managing available sessions.
 */
@Component({
  selector: 'app-besharp-session-selector',
  imports: [
    ButtonModule,
    CardModule,
    DividerModule,
    SplitButtonModule,
    FluidModule,
    MenuModule,
    InputGroupModule,
    StyleClassModule,
  ],
  templateUrl: './session-selector.component.html',
  styleUrls: ['./session-selector.component.scss'], // corrected styleUrl to styleUrls
  encapsulation: ViewEncapsulation.None,
})
export class SessionSelectorComponent implements OnInit {
  /**
   * Constructor for the SessionSelectorComponent.
   *
   * @param sessionsService Service for managing sessions.
   * @param router Router for navigating to different routes.
   */
  public constructor(
    protected sessionsService: SessionsService,
    protected router: Router
  ) {}

  /**
   * Map of dropdown menu content for each session.
   */
  protected dropDownMenuContent: Map<string, MenuItem[]> = new Map<string, MenuItem[]>();

  /**
   * List of available sessions.
   */
  protected sessions: Session[] = [];

  /**
   * ID of the currently active session.
   */
  protected activeSession: string | null = null;

  /**
   * Loads available sessions or navigates to the sign-in page if no sessions are available.
   *
   * @returns List of available sessions.
   */
  loadSessionsOrGoToSignIn() {
    const sessions = this.sessionsService.getSessions();

    // If there are no sessions to select, go to sign-in
    if (sessions.length === 0) {
      this.router.navigate(['session', 'signin'], { queryParams: { flags: SessionSignInFlag.SessionRequired } });
    }

    return sessions;
  }

  /**
   * Initializes the component by loading available sessions and setting up the dropdown menu.
   */
  async ngOnInit() {
    this.sessions = this.loadSessionsOrGoToSignIn();

    const newMenuContent = new Map<string, MenuItem[]>();
    for (const session of this.sessions) {
      newMenuContent.set(session.id, [
        {
          label: 'Sign out',
          command: (_) => {
            this.sessionsService.removeSession(session.id);
            this.sessions = this.loadSessionsOrGoToSignIn();
          },
          icon: 'pi pi-sign-out',
        },
      ]);
    }

    this.dropDownMenuContent = newMenuContent;
    this.sessions.sort((a, b) => (a.isExpired() ? 0 : 1) - (b.isExpired() ? 0 : 1));
    this.activeSession = this.sessionsService.activeSessionId;
  }

  /**
   * Handles the selection of a session.
   *
   * @param id ID of the selected session.
   */
  protected async onSessionSelect(id: string) {
    await this.sessionsService.setActiveSession(id);
  }

  /**
   * Returns the current timestamp.
   *
   * @returns Current timestamp in milliseconds.
   */
  protected getCurrentTimestamp(): number {
    return new Date().getTime();
  }
}
